import { 
  OceanDataSet, 
  OceanFeature, 
  Eddy, 
  WaterMassBoundary, 
  ConvergenceZone,
  FishingHotspot,
  SSTData 
} from './types';
import { hotspotRanker } from './hotspotRanker';

export class OceanAnalysisEngine {
  private static instance: OceanAnalysisEngine;
  
  private constructor() {}
  
  static getInstance(): OceanAnalysisEngine {
    if (!OceanAnalysisEngine.instance) {
      OceanAnalysisEngine.instance = new OceanAnalysisEngine();
    }
    return OceanAnalysisEngine.instance;
  }
  
  async analyzeOceanData(
    data: OceanDataSet,
    homePort?: { lat: number; lon: number },
    waypointOptions?: {
      useWaypoints?: boolean;
      waypointType?: 'public' | 'personal' | 'both';
      userId?: string;
    }
  ): Promise<{
    features: OceanFeature[];
    hotspots: FishingHotspot[];
  }> {
    console.log('[OceanAnalysisEngine] Starting ocean data analysis', waypointOptions);

    // Use the new streamlined ranking system with home port for practical clustering
    // Now includes optional waypoint proximity scoring
    const hotspots = await hotspotRanker.rankHotspots(data, homePort, waypointOptions);
    
    // Still detect features for additional context
    const features: OceanFeature[] = [];
    const eddies = await this.detectEddies(data);
    const fronts = await this.detectWaterMassBoundaries(data);
    const convergenceZones = await this.detectConvergenceZones(data);
    const chlorophyllFeatures = await this.detectChlorophyllGradients(data);
    
    console.log(`[OceanAnalysisEngine] Detected features: ${eddies.length} eddies, ${fronts.length} fronts, ${convergenceZones.length} convergence zones, ${chlorophyllFeatures.length} chlorophyll features`);
    
    features.push(...eddies, ...fronts, ...convergenceZones, ...chlorophyllFeatures);
    
    // Add nearby features to each hotspot
    hotspots.forEach(hotspot => {
      hotspot.nearbyFeatures = this.findNearbyFeatures(
        { type: 'hotspot', location: hotspot.location, strength: 1, confidence: 1 } as any,
        features,
        0.2
      );
    });
    
    console.log(`[OceanAnalysisEngine] Ranked ${hotspots.length} fishing hotspots`);
    
    return { features, hotspots };
  }
  
  private async detectEddies(data: OceanDataSet): Promise<Eddy[]> {
    console.log('[OceanAnalysisEngine] Detecting eddies');
    const eddies: Eddy[] = [];
    
    const { ssh, currents } = data;
    const threshold = 0.02; // Lowered from 0.1 to be more sensitive
    
    for (let i = 1; i < ssh.grid.length - 1; i++) {
      for (let j = 1; j < ssh.grid[i].length - 1; j++) {
        const center = ssh.grid[i][j];
        if (center === null) continue;
        
        const neighbors = [
          ssh.grid[i-1][j-1], ssh.grid[i-1][j], ssh.grid[i-1][j+1],
          ssh.grid[i][j-1],                      ssh.grid[i][j+1],
          ssh.grid[i+1][j-1], ssh.grid[i+1][j], ssh.grid[i+1][j+1]
        ];
        
        const validNeighbors = neighbors.filter(n => n !== null);
        if (validNeighbors.length < 6) continue;
        
        const avgNeighbor = validNeighbors.reduce((a, b) => a + b, 0) / validNeighbors.length;
        const anomaly = center - avgNeighbor;
        
        if (Math.abs(anomaly) > threshold) {
          const rotation = anomaly > 0 ? 'counterclockwise' : 'clockwise';
          const lat = data.bounds.south + (i / ssh.grid.length) * (data.bounds.north - data.bounds.south);
          const lon = data.bounds.west + (j / ssh.grid[0].length) * (data.bounds.east - data.bounds.west);
          
          
          const speed = currents.speedGrid[i]?.[j] || 0;
          
          eddies.push({
            type: 'eddy',
            location: { lat, lon },
            strength: Math.abs(anomaly),
            confidence: 0.7,
            properties: {
              rotation,
              radius: this.estimateEddyRadius(anomaly),
              speed,
              age: 0
            }
          });
        }
      }
    }
    
    return this.mergeNearbyEddies(eddies);
  }
  
  private async detectWaterMassBoundaries(data: OceanDataSet): Promise<WaterMassBoundary[]> {
    console.log('[OceanAnalysisEngine] Detecting water mass boundaries');
    const fronts: WaterMassBoundary[] = [];
    
    const { sst } = data;
    const gradientThreshold = 0.2; // Lowered from 0.5 to detect more subtle temperature changes
    
    for (let i = 1; i < sst.grid.length - 1; i++) {
      for (let j = 1; j < sst.grid[i].length - 1; j++) {
        const center = sst.grid[i][j];
        if (center === null) continue;
        
        const dx = (sst.grid[i][j+1] ?? center) - (sst.grid[i][j-1] ?? center);
        const dy = (sst.grid[i+1][j] ?? center) - (sst.grid[i-1][j] ?? center);
        
        const gradient = Math.sqrt(dx * dx + dy * dy);
        
        if (gradient > gradientThreshold) {
          const lat = data.bounds.south + (i / sst.grid.length) * (data.bounds.north - data.bounds.south);
          const lon = data.bounds.west + (j / sst.grid[0].length) * (data.bounds.east - data.bounds.west);
          
          
          const orientation = Math.atan2(dy, dx) * (180 / Math.PI);
          const warmSide = this.determineWarmSide(dx, dy);
          
          fronts.push({
            type: 'front',
            location: { lat, lon },
            strength: gradient,
            confidence: 0.6,
            properties: {
              gradient,
              orientation,
              length: 0,
              warmSide
            }
          });
        }
      }
    }
    
    return this.connectFronts(fronts);
  }
  
  private async detectConvergenceZones(data: OceanDataSet): Promise<ConvergenceZone[]> {
    console.log('[OceanAnalysisEngine] Detecting convergence zones');
    const zones: ConvergenceZone[] = [];
    
    const { currents, chlorophyll, bounds } = data;
    
    for (let i = 1; i < currents.uGrid.length - 1; i++) {
      for (let j = 1; j < currents.uGrid[i].length - 1; j++) {
        const u = currents.uGrid[i][j];
        const v = currents.vGrid[i][j];
        if (u === null || v === null) continue;
        
        const dudx = ((currents.uGrid[i][j+1] ?? u) - (currents.uGrid[i][j-1] ?? u)) / 2;
        const dvdy = ((currents.vGrid[i+1]?.[j] ?? v) - (currents.vGrid[i-1]?.[j] ?? v)) / 2;
        
        const divergence = dudx + dvdy;
        
        if (divergence < -0.00001) {
          const lat = bounds.south + (i / currents.uGrid.length) * (bounds.north - bounds.south);
          const lon = bounds.west + (j / currents.uGrid[0].length) * (bounds.east - bounds.west);
          
          
          const chlorValue = chlorophyll.grid[i]?.[j] || 0;
          const baitProbability = this.calculateBaitProbability(chlorValue, Math.abs(divergence));
          
          zones.push({
            type: 'convergence',
            location: { lat, lon },
            strength: Math.abs(divergence),
            confidence: 0.5,
            properties: {
              strength: Math.abs(divergence),
              area: 1,
              baitProbability
            }
          });
        }
      }
    }
    
    return zones;
  }
  
  private async identifyFishingHotspots(
    data: OceanDataSet, 
    features: OceanFeature[]
  ): Promise<FishingHotspot[]> {
    console.log('[OceanAnalysisEngine] Identifying fishing hotspots');
    const hotspots: FishingHotspot[] = [];
    
    for (const feature of features) {
      if (feature.confidence < 0.3) continue; // Lowered from 0.5
      
      const nearbyFeatures = this.findNearbyFeatures(feature, features, 0.1); // Reasonable radius for nearby features
      const score = this.calculateHotspotScore(feature, nearbyFeatures, data);
      
      if (score > 0.4) { // Lowered from 0.6
        const waterTemp = this.getTemperatureAtLocation(data.sst, feature.location, data.bounds);
        const targetSpecies = this.predictTargetSpecies(waterTemp, feature);
        
        hotspots.push({
          location: feature.location,
          confidence: feature.confidence,
          score,
          reasons: this.generateReasons(feature, nearbyFeatures),
          targetSpecies,
          optimalTime: {
            start: '06:00',
            end: '10:00',
            tidePhase: 'incoming'
          },
          waterConditions: {
            temperature: waterTemp,
            depth: undefined,
            clarity: undefined,
            salinity: undefined
          },
          nearbyFeatures
        });
      }
    }
    
    return this.rankHotspots(hotspots);
  }
  
  private estimateEddyRadius(anomaly: number): number {
    return Math.abs(anomaly) * 50;
  }
  
  private determineWarmSide(dx: number, dy: number): 'north' | 'south' | 'east' | 'west' {
    if (Math.abs(dx) > Math.abs(dy)) {
      return dx > 0 ? 'east' : 'west';
    } else {
      return dy > 0 ? 'north' : 'south';
    }
  }
  
  private calculateBaitProbability(chlorophyll: number, convergence: number): number {
    const chlorScore = Math.min(chlorophyll / 10, 1);
    const convergenceScore = Math.min(convergence * 10000, 1);
    return (chlorScore + convergenceScore) / 2;
  }
  
  private mergeNearbyEddies(eddies: Eddy[]): Eddy[] {
    const merged: Eddy[] = [];
    const used = new Set<number>();
    
    for (let i = 0; i < eddies.length; i++) {
      if (used.has(i)) continue;
      
      const eddy = eddies[i];
      const nearby: Eddy[] = [eddy];
      
      for (let j = i + 1; j < eddies.length; j++) {
        if (used.has(j)) continue;
        
        const dist = this.calculateDistance(eddy.location, eddies[j].location);
        if (dist < 0.05 && eddy.properties.rotation === eddies[j].properties.rotation) {
          nearby.push(eddies[j]);
          used.add(j);
        }
      }
      
      if (nearby.length > 1) {
        merged.push(this.mergeEddyGroup(nearby));
      } else {
        merged.push(eddy);
      }
    }
    
    return merged;
  }
  
  private connectFronts(fronts: WaterMassBoundary[]): WaterMassBoundary[] {
    return fronts;
  }
  
  private calculateDistance(loc1: { lat: number; lon: number }, loc2: { lat: number; lon: number }): number {
    const dlat = loc2.lat - loc1.lat;
    const dlon = loc2.lon - loc1.lon;
    return Math.sqrt(dlat * dlat + dlon * dlon);
  }
  
  private mergeEddyGroup(eddies: Eddy[]): Eddy {
    const avgLat = eddies.reduce((sum, e) => sum + e.location.lat, 0) / eddies.length;
    const avgLon = eddies.reduce((sum, e) => sum + e.location.lon, 0) / eddies.length;
    const maxStrength = Math.max(...eddies.map(e => e.strength));
    const avgRadius = eddies.reduce((sum, e) => sum + e.properties.radius, 0) / eddies.length;
    
    return {
      type: 'eddy',
      location: { lat: avgLat, lon: avgLon },
      strength: maxStrength,
      confidence: 0.8,
      properties: {
        rotation: eddies[0].properties.rotation,
        radius: avgRadius * 1.5,
        speed: Math.max(...eddies.map(e => e.properties.speed)),
        age: 0
      }
    };
  }
  
  private findNearbyFeatures(
    feature: OceanFeature, 
    allFeatures: OceanFeature[], 
    radius: number
  ): OceanFeature[] {
    return allFeatures.filter(f => {
      if (f === feature) return false;
      const dist = this.calculateDistance(feature.location, f.location);
      return dist <= radius && dist > 0.01; // Exclude very close duplicates
    }).slice(0, 5); // Limit to 5 nearby features max
  }
  
  private calculateHotspotScore(
    feature: OceanFeature, 
    nearbyFeatures: OceanFeature[], 
    data: OceanDataSet
  ): number {
    let score = 0;
    
    if (feature.type === 'eddy') score += 0.3;
    if (feature.type === 'front') score += 0.4;
    if (feature.type === 'convergence') {
      score += 0.3;
      // Bonus for high bait probability
      if (feature.properties && 'baitProbability' in feature.properties) {
        score += feature.properties.baitProbability * 0.2;
      }
    }
    
    score += Math.min(nearbyFeatures.length * 0.1, 0.3);
    
    score *= feature.confidence;
    
    return Math.min(score, 1);
  }
  
  private getTemperatureAtLocation(
    sst: SSTData, 
    location: { lat: number; lon: number }, 
    bounds: any
  ): number {
    const i = Math.floor((location.lat - bounds.south) / (bounds.north - bounds.south) * sst.grid.length);
    const j = Math.floor((location.lon - bounds.west) / (bounds.east - bounds.west) * sst.grid[0].length);
    
    if (i >= 0 && i < sst.grid.length && j >= 0 && j < sst.grid[0].length) {
      return sst.grid[i][j] || 20;
    }
    
    return 20;
  }
  
  private predictTargetSpecies(temperature: number, feature: OceanFeature): string[] {
    const species: string[] = [];
    
    if (temperature >= 24 && temperature <= 28) {
      species.push('Mahi Mahi', 'Wahoo', 'Tuna');
    } else if (temperature >= 20 && temperature < 24) {
      species.push('Sailfish', 'King Mackerel', 'Cobia');
    } else if (temperature < 20) {
      species.push('Grouper', 'Snapper', 'Amberjack');
    }
    
    if (feature.type === 'eddy') {
      species.push('Marlin');
    }
    
    return [...new Set(species)];
  }
  
  private generateReasons(feature: OceanFeature, nearbyFeatures: OceanFeature[]): string[] {
    const reasons: string[] = [];
    
    switch (feature.type) {
      case 'eddy':
        reasons.push(`${feature.properties.rotation} eddy with strong circulation`);
        break;
      case 'front':
        reasons.push(`Temperature front with ${feature.properties.gradient.toFixed(1)}°C gradient`);
        break;
      case 'convergence':
        if (feature.properties && 'baitProbability' in feature.properties) {
          const baitLevel = feature.properties.baitProbability > 0.7 ? 'high' : 
                           feature.properties.baitProbability > 0.4 ? 'moderate' : 'low';
          reasons.push(`Convergence zone with ${baitLevel} bait probability`);
          if (feature.strength > 1.5) {
            reasons.push(`High chlorophyll concentration (${feature.strength.toFixed(1)} mg/m³)`);
          }
        } else {
          reasons.push(`Convergence zone with potential bait concentration`);
        }
        break;
    }
    
    if (nearbyFeatures.length > 0) {
      reasons.push(`${nearbyFeatures.length} additional ocean features nearby`);
    }
    
    return reasons;
  }
  
  private rankHotspots(hotspots: FishingHotspot[]): FishingHotspot[] {
    return hotspots.sort((a, b) => b.score - a.score);
  }
  
  private async identifyHotspotsFromTemperature(data: OceanDataSet): Promise<FishingHotspot[]> {
    const hotspots: FishingHotspot[] = [];
    const { sst, bounds } = data;
    
    // Find areas with good fishing temperatures (24-30°C for Gulf)
    const optimalTempMin = 24;
    const optimalTempMax = 30;
    
    // Sample the grid to find promising areas
    const stepSize = Math.max(Math.floor(sst.grid.length / 5), 1);
    
    for (let i = 0; i < sst.grid.length; i += stepSize) {
      for (let j = 0; j < sst.grid[i].length; j += stepSize) {
        const temp = sst.grid[i][j];
        if (temp === null) continue;
        
        // Check if temperature is in optimal range
        if (temp >= optimalTempMin && temp <= optimalTempMax) {
          const lat = bounds.south + (i / sst.grid.length) * (bounds.north - bounds.south);
          const lon = bounds.west + (j / sst.grid[0].length) * (bounds.east - bounds.west);
          
          
          // Check for temperature variations nearby (fish like edges)
          const tempVariation = this.calculateLocalTemperatureVariation(sst.grid, i, j);
          
          if (tempVariation > 0.3) { // Some temperature variation is good
            const targetSpecies = this.predictTargetSpecies(temp, undefined);
            
            hotspots.push({
              location: { lat, lon },
              confidence: 0.6,
              score: 0.5 + (tempVariation * 0.3),
              reasons: [
                `Water temperature ${temp.toFixed(1)}°C ideal for fishing`,
                tempVariation > 0.5 ? 'Temperature edge detected' : 'Stable temperature zone'
              ],
              targetSpecies,
              optimalTime: {
                start: '06:00',
                end: '10:00',
                tidePhase: 'incoming'
              },
              waterConditions: {
                temperature: temp,
                depth: undefined,
                clarity: undefined,
                salinity: undefined
              },
              nearbyFeatures: []
            });
          }
        }
      }
    }
    
    // Return top 5 hotspots
    return this.rankHotspots(hotspots).slice(0, 5);
  }
  
  private calculateLocalTemperatureVariation(grid: number[][], i: number, j: number): number {
    const neighbors: number[] = [];
    const radius = 2;
    
    for (let di = -radius; di <= radius; di++) {
      for (let dj = -radius; dj <= radius; dj++) {
        const ni = i + di;
        const nj = j + dj;
        if (ni >= 0 && ni < grid.length && nj >= 0 && nj < grid[ni].length) {
          const val = grid[ni][nj];
          if (val !== null) neighbors.push(val);
        }
      }
    }
    
    if (neighbors.length < 2) return 0;
    
    const min = Math.min(...neighbors);
    const max = Math.max(...neighbors);
    return max - min;
  }
  
  private async detectChlorophyllGradients(data: OceanDataSet): Promise<OceanFeature[]> {
    console.log('[OceanAnalysisEngine] Detecting chlorophyll gradients');
    const features: OceanFeature[] = [];
    
    const { chlorophyll, bounds } = data;
    const gradientThreshold = 0.3; // mg/m³ change threshold
    
    // Analyze chlorophyll concentration gradients
    for (let i = 1; i < chlorophyll.grid.length - 1; i++) {
      for (let j = 1; j < chlorophyll.grid[i].length - 1; j++) {
        const center = chlorophyll.grid[i][j];
        if (center === null || center === 0) continue;
        
        // Calculate gradient
        const dx = (chlorophyll.grid[i][j+1] ?? center) - (chlorophyll.grid[i][j-1] ?? center);
        const dy = (chlorophyll.grid[i+1][j] ?? center) - (chlorophyll.grid[i-1][j] ?? center);
        const gradient = Math.sqrt(dx * dx + dy * dy);
        
        // High chlorophyll gradient indicates productivity edge
        if (gradient > gradientThreshold) {
          const lat = bounds.south + (i / chlorophyll.grid.length) * (bounds.north - bounds.south);
          const lon = bounds.west + (j / chlorophyll.grid[0].length) * (bounds.east - bounds.west);
          
          
          features.push({
            type: 'convergence',
            location: { lat, lon },
            strength: gradient,
            confidence: 0.7,
            properties: {
              strength: gradient,
              area: 1,
              baitProbability: Math.min(center / 2, 1) // Higher chlorophyll = more bait
            }
          });
        }
        
        // Also look for chlorophyll hotspots (high concentration areas)
        if (center > 1.5) { // mg/m³ - productive water
          const lat = bounds.south + (i / chlorophyll.grid.length) * (bounds.north - bounds.south);
          const lon = bounds.west + (j / chlorophyll.grid[0].length) * (bounds.east - bounds.west);
          
          // Check if it's a local maximum
          const neighbors = [
            chlorophyll.grid[i-1][j-1], chlorophyll.grid[i-1][j], chlorophyll.grid[i-1][j+1],
            chlorophyll.grid[i][j-1],                               chlorophyll.grid[i][j+1],
            chlorophyll.grid[i+1][j-1], chlorophyll.grid[i+1][j], chlorophyll.grid[i+1][j+1]
          ].filter(n => n !== null);
          
          const avgNeighbor = neighbors.reduce((a, b) => a + b, 0) / neighbors.length;
          
          if (center > avgNeighbor * 1.2) { // 20% higher than surroundings
            // Check if we're over water (rough check for Gulf)
            if (lon > -97.2) continue; // Skip land coordinates
            
            features.push({
              type: 'convergence',
              location: { lat, lon },
              strength: center,
              confidence: 0.8,
              properties: {
                strength: center,
                area: 2,
                baitProbability: Math.min(center / 1.5, 1)
              }
            });
          }
        }
      }
    }
    
    return features;
  }

}

export const oceanAnalysisEngine = OceanAnalysisEngine.getInstance();